import React from "react";
import { colors, fonts, space, radius } from "../theme";

const money = (n) => `$${Number(n).toFixed(2)}`;
const PAY_TEXT = (o) => {
  if (o.payment.status === "paid") return o.payment.method === "stripe" ? "Paid online" : "Paid";
  if (o.payment.status === "pay_at_pickup") return "Pay at pickup";
  if (o.payment.status === "refunded") return "Refunded";
  return o.payment.method === "stripe" ? "Waiting for card payment" : "Payment collected by the restaurant";
};

// One order's live status: the message, a progress bar, the items and total. Used for the customer's
// confirmation and for order tracking.
export default function OrderStatusCard({ order, onCancel, highlight }) {
  const dead = ["cancelled", "expired", "no_show"].includes(order.status);
  const note = order.note || {};
  const tone = dead ? { bg: "#fef3f2", fg: "#b42318" } : order.status === "ready" ? { bg: "#ecfdf3", fg: "#067647" } : note.kind === "payment_needed" ? { bg: "#fffaeb", fg: "#93370d" } : { bg: "#eff6ff", fg: "#1d4ed8" };
  const renderText = () => {
    if (note.call && note.text.includes(note.call)) {
      const [a, b] = note.text.split(note.call);
      return <>{a}<a href={`tel:${note.call.replace(/\D/g, "")}`} style={{ color: "inherit", fontWeight: 700 }}>{note.call}</a>{b}</>;
    }
    return note.text;
  };
  return (
    <div data-testid="order-card" style={{ ...styles.card, ...(highlight ? { borderColor: "var(--ac, #2f6fed)" } : {}) }}>
      <div style={styles.head}>
        <div>
          <div style={styles.small}>Order</div>
          <div style={styles.number} data-testid="order-number">{order.order_number}</div>
        </div>
        <span style={{ ...styles.pill, background: tone.bg, color: tone.fg }} data-testid="order-status">{order.status_label}</span>
      </div>

      {(note.title || note.text) && (
        <div role="status" style={{ ...styles.note, background: tone.bg, color: tone.fg }} data-testid="order-note" data-kind={note.kind}>
          {note.title && <strong>{note.title} </strong>}
          {renderText()}
        </div>
      )}

      {!dead && (
        <div style={styles.steps} aria-label="Order progress">
          {order.steps.map((label, i) => (
            <div key={label} style={{ ...styles.step, opacity: i <= order.step ? 1 : 0.45 }}>
              <div style={{ ...styles.dot, background: i <= order.step ? "var(--ac, #2f6fed)" : "var(--ln, #e5e7eb)" }} />
              <div style={styles.stepLabel}>{label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={styles.meta}><span>Pickup</span><strong>{order.pickup.text}</strong></div>
      <div style={styles.items}>
        {order.items.map((it, i) => (
          <div key={i} style={styles.itemRow}><span>{it.qty} × {it.name}{it.notes ? <em style={{ color: "var(--mu, #6b7280)" }}> ({it.notes})</em> : null}</span></div>
        ))}
      </div>
      <div style={styles.meta}><span>{PAY_TEXT(order)}</span><strong>{money(order.total)}</strong></div>
      {order.can_cancel && onCancel && <button style={styles.cancel} onClick={() => onCancel(order)}>Cancel this order</button>}
    </div>
  );
}

const styles = {
  card: { background: "var(--card, #fff)", border: "2px solid var(--ln, #e5e7eb)", borderRadius: "var(--r, 16px)", padding: space[4], marginBottom: space[3], fontFamily: "var(--bf, inherit)", color: "var(--tx, #1f2937)", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: space[2], flexWrap: "wrap" },
  small: { fontSize: 11, color: "var(--mu, #6b7280)", textTransform: "uppercase", letterSpacing: "0.06em" },
  number: { fontFamily: "var(--hf, inherit)", fontSize: 24, fontWeight: 700, letterSpacing: "0.04em" },
  pill: { borderRadius: 999, padding: "4px 12px", fontSize: 13, fontWeight: 600 },
  note: { marginTop: space[3], padding: space[3], borderRadius: radius.md, fontSize: 14, lineHeight: 1.45 },
  steps: { display: "flex", justifyContent: "space-between", margin: `${space[4]} 0 ${space[2]}` },
  step: { flex: 1, textAlign: "center" },
  dot: { width: 14, height: 14, borderRadius: 999, margin: "0 auto 4px" },
  stepLabel: { fontSize: 10, color: "var(--mu, #6b7280)" },
  meta: { display: "flex", justifyContent: "space-between", gap: space[2], fontSize: 14, padding: `${space[2]} 0`, borderTop: "1px solid var(--ln, #e5e7eb)", marginTop: space[2] },
  items: { fontSize: 14, color: "var(--mu, #6b7280)", marginTop: space[2] },
  itemRow: { padding: "2px 0" },
  cancel: { marginTop: space[3], width: "100%", padding: "10px", borderRadius: radius.md, border: "1px solid #fca5a5", background: "#fff", color: "#b42318", fontSize: 14, cursor: "pointer" },
};
